"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { LeaderboardTournament } from "@/queries/course-tournament";
import { TournamentBirdieStreak } from "@/queries/tournament-birdie-streak";
import { TournamentClosestToPin } from "@/queries/tournament-closest-to-pin";
import { TournamentGreensInRegulation } from "@/queries/tournament-gir";
import { TournamentLeaderboard } from "@/queries/tournament-leaderboard";
import { TournamentLongestDrive } from "@/queries/tournament-longest-drive";
import { GOLF_PORTAL_API } from "@/utils/constants";
import StrokeplayLeaderboard from "./StrokeplayLeaderboard";
import CTPLeaderboard from "./CTPLeaderboard";
import LongestDriveLeaderboard from "./LongestDriveLeaderboard";
import BirdieStreakLeaderboard from "./BirdieStreakLeadearboard";
import GIRLeaderboard from "./GIRLeaderboard";
import CTPHoleLeaderboard from "./CTPHoleLeaderboard";
import Cta from "../Cta";
import { QueryVariablesType } from "../LeaderboardWrapper";
import { TournamentClosestToPinInRound } from "@/queries/tournament-ctp-in-round";
import { TournamentQuery } from "@/types/tournament-query";
import { TournamentClosestToPinRange } from "@/queries/tournament-ctp-range";
import { LeaderboardCTPRangeTournament } from "@/queries/ctp-range-tournament";
import CTPRangeLeaderboard from "./CTPRangeLeaderboard";

const TOURNAMENT_QUERY = {
  leaderboard: TournamentLeaderboard,
  aggregatedClosestToPinLeaderboard: TournamentClosestToPin,
  closestToPinLeaderboard: TournamentClosestToPinInRound,
  birdieStreak: TournamentBirdieStreak,
  longestDrive: TournamentLongestDrive,
  greensInRegulation: TournamentGreensInRegulation,
  ctpRangeLeaderboard: TournamentClosestToPinRange,
};

interface LeaderboardProps {
  tournamentQueryKey: string | number;
  tournamentId: string;
  tournamentQuery: TournamentQuery;
  queryVariables?: QueryVariablesType;
  tournamentName?: string;
  isMultiRoundTournament: boolean;
  isCTPRangeTournament: boolean;
}

const Leaderboard = ({
  tournamentQueryKey,
  tournamentId,
  tournamentQuery,
  queryVariables,
  tournamentName,
  isMultiRoundTournament,
  isCTPRangeTournament,
}: LeaderboardProps) => {
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [favouritesList, setFavouritesList] = useState<string[]>([]);

  const pathname = usePathname();
  const mode = isCTPRangeTournament ? "range" : "stroke";

  const { data: tournamentData, isLoading: tournamentDataLoading } = useQuery(
    ["tournament", tournamentId],
    () => {
      return axios.post(GOLF_PORTAL_API, {
        query: isCTPRangeTournament
          ? LeaderboardCTPRangeTournament
          : LeaderboardTournament,
        variables: {
          tournamentId,
        },
      });
    },
    {
      enabled: typeof pathname === "string" && !pathname.includes("playerid"),
      refetchOnWindowFocus: false,
    },
  );

  const rounds = tournamentData?.data?.data?.node?.rounds;

  const tournamentRoundId = useMemo(() => {
    if (!rounds?.length) return null;

    return {
      firstRoundId: rounds[0].id,
      secondRoundId: rounds[1]?.id ?? null,
    };
  }, [rounds]);

  const longestDriveHole = useMemo(() => {
    return rounds?.[0]?.embeddedGame?.longestDrive?.holes?.[0] ?? null;
  }, [rounds]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    isRefetching,
    isError,
  } = useInfiniteQuery(
    [
      "tournament_leaderboard",
      tournamentQueryKey,
      queryVariables,
      tournamentRoundId,
      longestDriveHole,
      mode,
      tournamentId,
    ],
    ({ pageParam = 0 }) => {
      return axios.post(GOLF_PORTAL_API, {
        query: TOURNAMENT_QUERY[tournamentQuery as keyof {}],
        variables: {
          holeNumber: longestDriveHole,
          playerIds: favouritesList,
          tournamentId,
          publishedTournamentId: tournamentId,
          roundId:
            (tournamentQuery === "aggregatedClosestToPinLeaderboard" ||
              tournamentQuery === "closestToPinLeaderboard") &&
            isMultiRoundTournament
              ? tournamentRoundId?.secondRoundId
              : tournamentRoundId?.firstRoundId,
          skip: pageParam === 0 ? 0 : 50,
          take: pageParam === 0 ? 50 : 8000,
          ...queryVariables,
        },
      });
    },
    {
      enabled:
        !tournamentDataLoading && (isCTPRangeTournament || !!tournamentRoundId),
      refetchOnWindowFocus: false,
      getNextPageParam: (_lastPage, pages) => {
        const dataNode = pages[pages.length - 1].data.data.node;
        if (
          ((tournamentQuery === "leaderboard" ||
            tournamentQuery === "ctpRangeLeaderboard") &&
            dataNode?.leaderboard?.records?.pageInfo?.hasNextPage) ||
          (tournamentQuery === "aggregatedClosestToPinLeaderboard" &&
            dataNode?.aggregatedClosestToPinLeaderboard?.records?.pageInfo
              ?.hasNextPage) ||
          ((tournamentQuery === "birdieStreak" ||
            tournamentQuery === "greensInRegulation") &&
            dataNode?.otherLeaderboards?.records?.pageInfo?.hasNextPage) ||
          ((tournamentQuery === "longestDrive" ||
            tournamentQuery === "closestToPinLeaderboard") &&
            dataNode?.embeddedGameLeaderboard?.records?.pageInfo?.hasNextPage)
        ) {
          return pages.length;
        } else {
          return undefined;
        }
      },
    },
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouritesList]);

  let totalCount;
  let currentCount;

  if (
    tournamentQuery === "leaderboard" ||
    tournamentQuery === "ctpRangeLeaderboard"
  ) {
    totalCount =
      data?.pages?.[0]?.data?.data?.node?.leaderboard?.records?.totalCount;
    currentCount =
      data?.pages?.[0]?.data?.data?.node?.leaderboard?.records?.items?.length;
  }
  if (tournamentQuery === "aggregatedClosestToPinLeaderboard") {
    totalCount =
      data?.pages?.[0]?.data?.data?.node?.aggregatedClosestToPinLeaderboard
        ?.records?.totalCount;
    currentCount =
      data?.pages?.[0]?.data?.data?.node?.aggregatedClosestToPinLeaderboard
        ?.records?.items?.length;
  }
  if (tournamentQuery === "birdieStreak") {
    totalCount =
      data?.pages?.[0]?.data?.data?.node?.otherLeaderboards?.records
        ?.totalCount;
    currentCount =
      data?.pages?.[0]?.data?.data?.node?.otherLeaderboards?.records?.items
        ?.length;
  }
  if (
    tournamentQuery === "longestDrive" ||
    tournamentQuery === "closestToPinLeaderboard"
  ) {
    totalCount =
      data?.pages?.[0]?.data?.data?.node?.embeddedGameLeaderboard?.records
        ?.totalCount;
    currentCount =
      data?.pages?.[0]?.data?.data?.node?.embeddedGameLeaderboard?.records
        ?.items?.length;
  }
  if (tournamentQuery === "greensInRegulation") {
    totalCount =
      data?.pages?.[0]?.data?.data?.node?.otherLeaderboards?.records
        ?.totalCount;
    currentCount =
      data?.pages?.[0]?.data?.data?.node?.otherLeaderboards?.records?.items
        ?.length;
  }

  const loading =
    isLoading || isFetchingNextPage || isRefetching || tournamentDataLoading;

  const Error = () => {
    return isError ? (
      <h2 className="text-tm-c-dark opacity-60 text-[25px] mt-[160px] mx-10 mb-0 text-center">
        Oops.. something went wrong. Please try again later.
      </h2>
    ) : null;
  };

  return (
    <div className="pb-20">
      {tournamentQuery === "leaderboard" && (
        <StrokeplayLeaderboard
          isMultiRoundTournament={isMultiRoundTournament}
          data={data}
          gender={queryVariables?.gender}
          isLoading={loading}
          ErrorMessage={Error}
          favouritesList={favouritesList}
          setFavouritesList={setFavouritesList}
          refetchFavourites={refetch}
          favoritePlayers={
            data?.pages[0]?.data?.data?.node?.leaderboard?.selectedPlayers
          }
          tournamentName={tournamentName}
        />
      )}
      {tournamentQuery === "aggregatedClosestToPinLeaderboard" && (
        <CTPLeaderboard
          isMultiRoundTournament={isMultiRoundTournament}
          tournamentData={tournamentData}
          data={data}
          gender={queryVariables?.gender}
          searchText={queryVariables?.searchText}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={setSelectedPlayers}
          isLoading={loading}
          ErrorMessage={Error}
        />
      )}
      {tournamentQuery === "closestToPinLeaderboard" && (
        <CTPHoleLeaderboard
          data={data}
          gender={queryVariables?.gender}
          searchText={queryVariables?.searchText}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={setSelectedPlayers}
          isLoading={loading}
          ErrorMessage={Error}
        />
      )}
      {tournamentQuery === "longestDrive" && (
        <LongestDriveLeaderboard
          data={data}
          isLoading={loading}
          ErrorMessage={Error}
        />
      )}
      {tournamentQuery === "birdieStreak" && (
        <BirdieStreakLeaderboard
          data={data}
          isLoading={loading}
          ErrorMessage={Error}
        />
      )}
      {tournamentQuery === "greensInRegulation" && (
        <GIRLeaderboard data={data} isLoading={loading} ErrorMessage={Error} />
      )}
      {tournamentQuery === "ctpRangeLeaderboard" && (
        <CTPRangeLeaderboard
          tournamentData={tournamentData}
          data={data}
          gender={queryVariables?.gender}
          searchText={queryVariables?.searchText}
          selectedPlayers={selectedPlayers}
          setSelectedPlayers={setSelectedPlayers}
          isLoading={loading}
          ErrorMessage={Error}
        />
      )}
      <div className="mt-12 text-center">
        <p className="text-xs">
          Showing{" "}
          {loading ? (
            <span
              className="inline-block h-[25px] bg-tm-c-dark opacity-30 w-[60%] rounded-[10px]"
              style={{ background: "#ccc", width: "30px", height: "18px" }}
            />
          ) : hasNextPage ? (
            currentCount
          ) : (
            totalCount
          )}{" "}
          from {totalCount}
        </p>
      </div>
      {hasNextPage && (
        <Cta
          className="mt-2.5 mx-auto"
          onClick={() => fetchNextPage()}
          disabled={loading}
          name="Show all"
        />
      )}
    </div>
  );
};

export default Leaderboard;
